import { useRef, type ChangeEvent } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { Alert, Card, Input } from '@/components/ui';
import { cn } from '@/lib/cn';
import { normalisePincode, type DeliveryDetailsValues } from '@/lib/validators';

import { LABEL_PRESETS } from './model';

export interface AddressDetailsFieldsProps {
  form: UseFormReturn<DeliveryDetailsValues>;
  /**
   * Whether to ask for the area and the PIN code. Normally the map answered
   * both and neither is shown; a pin the geocoder could not name turns them
   * on, with the hint saying why.
   */
  needsArea: boolean;
  needsPincode: boolean;
  /** Field messages from a server 422, keyed by field name. */
  serverFieldErrors?: Record<string, string>;
  disabled?: boolean;
  /**
   * Labels the customer's OTHER addresses already use. A name can belong to
   * one address: a taken chip is greyed out, and a typed name that matches
   * one is refused before it reaches the server (which refuses it too).
   */
  takenLabels?: readonly string[];
}

/**
 * What a customer TYPES about a delivery address — shared by the checkout and
 * the account page's editor, so the two cannot drift apart.
 *
 * ONE REQUIRED LINE: the flat or house number and the floor. Everything else
 * is optional or already filled in. See `deliveryDetailsSchema` for why the
 * city and PIN code are not asked for unless the map came up short.
 */
export function AddressDetailsFields({
  form,
  needsArea,
  needsPincode,
  serverFieldErrors = {},
  disabled = false,
  takenLabels = [],
}: AddressDetailsFieldsProps) {
  const { register, watch, setValue, formState } = form;
  const errorFor = (field: keyof DeliveryDetailsValues): string | undefined =>
    formState.errors[field]?.message ?? serverFieldErrors[field];

  const label = watch('label') ?? '';
  const usingPresetLabel = (LABEL_PRESETS as readonly string[]).includes(label);
  const taken = (candidate: string) =>
    takenLabels.some((used) => used.trim().toLowerCase() === candidate.trim().toLowerCase());
  const customLabelRef = useRef<HTMLInputElement | null>(null);

  return (
    <>
      <Card title="Address details">
        <div className="flex flex-col gap-4">
          {serverFieldErrors['__all__'] ? (
            <Alert severity="danger" title="Please check these details" live>
              <p>{serverFieldErrors['__all__']}</p>
            </Alert>
          ) : null}

          <Input
            {...register('line1')}
            label="Flat / house number and floor"
            autoComplete="address-line1"
            required
            disabled={disabled}
            error={errorFor('line1')}
          />
          <Input
            {...register('landmark')}
            label="Landmark (optional)"
            hint="Anything that helps the rider find the door."
            autoComplete="address-line2"
            disabled={disabled}
            error={errorFor('landmark')}
          />

          {/* Asked only when the map could not say. No explanation attached:
              the customer wants the box, not the reason. */}
          {needsArea ? (
            <Input
              {...register('city')}
              label="Area or town"
              autoComplete="address-level2"
              required
              disabled={disabled}
              error={errorFor('city')}
            />
          ) : null}
          {needsPincode ? (
            <Input
              {...register('pincode', {
                onChange: (event: ChangeEvent<HTMLInputElement>) => {
                  setValue('pincode', normalisePincode(event.target.value), {
                    shouldValidate: false,
                  });
                },
              })}
              label="PIN code"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={6}
              required
              disabled={disabled}
              error={errorFor('pincode')}
            />
          ) : null}

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium text-ink">Save as</legend>
            <div className="flex flex-wrap gap-2">
              {LABEL_PRESETS.map((preset) => (
                <LabelChip
                  key={preset}
                  selected={label === preset}
                  onClick={() => setValue('label', label === preset ? '' : preset)}
                  disabled={disabled || taken(preset)}
                  title={taken(preset) ? `You already have an address called ${preset}` : undefined}
                >
                  {preset}
                </LabelChip>
              ))}
              <LabelChip
                selected={label !== '' && !usingPresetLabel}
                onClick={() => {
                  setValue('label', label !== '' && !usingPresetLabel ? '' : 'Other');
                  // Let them replace the placeholder word straight away.
                  setTimeout(() => customLabelRef.current?.select(), 0);
                }}
                disabled={disabled}
              >
                Other
              </LabelChip>
            </div>
            {label !== '' && !usingPresetLabel ? (
              <Input
                {...register('label')}
                ref={(node) => {
                  register('label').ref(node);
                  customLabelRef.current = node;
                }}
                label="Name for this address"
                labelHidden
                placeholder="e.g. Mum's place"
                maxLength={24}
                disabled={disabled}
                error={errorFor('label')}
              />
            ) : null}
          </fieldset>
        </div>
      </Card>

      <Card title="Who receives it">
        <div className="flex flex-col gap-4">
          <Input
            {...register('name')}
            label="Name"
            autoComplete="name"
            required
            disabled={disabled}
            error={errorFor('name')}
          />
          <Input
            {...register('phone')}
            label="Mobile number"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            prefix="+91"
            maxLength={13}
            required
            disabled={disabled}
            hint="The rider calls this number."
            error={errorFor('phone')}
          />
        </div>
      </Card>
    </>
  );
}

function LabelChip({
  selected,
  onClick,
  disabled,
  title,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'min-h-touch rounded-full border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2',
        selected
          ? 'border-brand-ink bg-brand-soft text-ink ring-1 ring-brand-ink'
          : 'border-line bg-surface text-ink-muted hover:bg-surface-sunken',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {children}
    </button>
  );
}
